import { fetchData } from 'Utils/fetch-data';
import graphql from './graphql';
import type { MyCourseI, MyCoursesSummaryI, MyCourseStatusFilterT, DashboardCourseI } from 'types';

const MY_COURSES_ENDPOINT = '/users/me/courses';
const STATUS_FILTERS: MyCourseStatusFilterT[] = ['pending', 'approved', 'failed'];
const PAGE_LIMIT = 100;

const EMPTY_SUMMARY: MyCoursesSummaryI = { pending: 0, approved: 0, failed: 0, total: 0 };

export interface MyCoursesFiltersI {
  category?: string | null;
  name?: string | null;
}

export interface MyCoursesDataI {
  courses: DashboardCourseI[];
  summary: MyCoursesSummaryI;
}

/**
 * Trae los cursos del empleado autenticado desde GET /users/me/courses.
 * El endpoint no entrega estatus por item, así que se consulta una vez por
 * filtro (pending/approved/failed) y se etiqueta cada curso con su origen.
 * El orden global sale de la concatenación: pending → approved → failed.
 * category y name se mandan como query params, pero el backend aún no los
 * soporta; mientras tanto se aplica un fallback client-side con la misma
 * semántica del legacy GraphQL: name por substring case-insensitive sobre
 * el título y category por igualdad con el nombre de la categoría. Cuando
 * el backend filtre, el doble filtro es inocuo. El summary del meta no se
 * toca: siempre es el panorama completo sin filtros.
 * El thumbnail ya llega como URL firmada (expira en 1h, no se persiste).
 */
export const getMyCourses = async (
  filters: MyCoursesFiltersI = {}
): Promise<{ data: MyCoursesDataI | null; error: string | null }> => {
  const optionalParams: Record<string, string> = {};
  if (filters.category) optionalParams.category = filters.category;
  if (filters.name) optionalParams.name = filters.name;

  const responses = await Promise.all(
    STATUS_FILTERS.map(status =>
      fetchData<MyCourseI[]>(MY_COURSES_ENDPOINT, {
        method: 'GET',
        params: { status, limit: PAGE_LIMIT, ...optionalParams },
        includeJsonApiMeta: true,
      })
    )
  );

  if (responses.some(response => response.error !== null || response.data === null))
    return { data: null, error: 'FETCH_ERROR' };

  // meta.summary siempre trae el panorama completo, ignora el filtro
  const summary = (responses[0].meta as { summary?: MyCoursesSummaryI } | null)?.summary ?? EMPTY_SUMMARY;

  // Fallback client-side mientras el backend ignora name/category
  const nameNeedle = filters.name?.toLowerCase() ?? null;
  const matchesFilters = (course: MyCourseI): boolean => {
    if (nameNeedle && !course.title?.toLowerCase().includes(nameNeedle)) return false;
    if (filters.category && course.category?.name !== filters.category) return false;
    return true;
  };

  // El endpoint no excluye borradores; la página no los muestra
  const courses: DashboardCourseI[] = STATUS_FILTERS.flatMap((status, index) =>
    (responses[index].data ?? [])
      .filter(course => !course.isDraft && matchesFilters(course))
      .map(course => ({ ...course, status }))
  );

  return { data: { courses, summary }, error: null };
};

interface CategoryListResponseI {
  CourseCategory_list: { name: string | null }[];
}

export const getCategories = async (): Promise<{ data: string[]; error: string | null }> => {
  try {
    const response = (await graphql('CourseCategory_list', 'name', 'query')) as CategoryListResponseI & {
      error?: boolean;
    };
    // graphql() no rechaza en errores de request: resuelve con { error: true, ... }
    if (response.error || !Array.isArray(response.CourseCategory_list)) return { data: [], error: 'FETCH_ERROR' };
    return { data: response.CourseCategory_list.map(category => category.name || ''), error: null };
  } catch {
    return { data: [], error: 'FETCH_ERROR' };
  }
};
